import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, Utensils, Leaf, Truck, ChefHat, Heart, Star } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.12 + i * 0.09 } }),
};

const BADGES = [
  { icon: Leaf, title: "100% Halal", sub: "Always" },
  { icon: ChefHat, title: "Made Fresh", sub: "Daily" },
  { icon: Truck, title: "Pickup &", sub: "Delivery" },
  { icon: Utensils, title: "Custom", sub: "Menus" },
];

const HERO_IMAGES = [
  { src: "https://images.unsplash.com/photo-1589302168068-964664d93dc0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", alt: "Goat biryani platter", name: "Goat Biryani", price: "Full Tray · $160" },
  { src: "https://images.unsplash.com/photo-1631292784640-2b24be784d5d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", alt: "Chicken karahi", name: "Chicken Karahi", price: "Full Tray · $130" },
  { src: "https://images.unsplash.com/photo-1606843046080-45bf7a23c39f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", alt: "Goat nihari", name: "Goat Nihari", price: "Full Tray · $170" },
  { src: "https://images.unsplash.com/photo-1631515243349-e0cb75fb8d3a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", alt: "Chicken biryani", name: "Chicken Biryani", price: "Full Tray · $130" },
];

const DECOR = [
  "https://images.unsplash.com/photo-1656568866961-03e9dcc0fbc6?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  "https://images.unsplash.com/photo-1701166627787-12d9fdd437cc?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  "https://images.unsplash.com/photo-1625921133217-8d978f7872b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_IMAGES.length), 3400);
    return () => clearInterval(t);
  }, []);
  const active = HERO_IMAGES[idx];

  return (
    <section ref={ref} data-testid="hero" className="relative overflow-hidden bg-nosh-cream pt-32 md:pt-36">
      <span aria-hidden="true" className="pointer-events-none absolute -right-10 top-20 select-none font-display text-[18vw] font-bold italic leading-none text-nosh-green/[0.04]">
        Nosh
      </span>

      {/* soft background decor to fill the canvas */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -left-28 top-16 h-80 w-80 rounded-full bg-nosh-green/[0.06] blur-3xl" />
        <div className="absolute left-[6%] bottom-2 h-56 w-56 rounded-full bg-nosh-gold/[0.06] blur-3xl" />
        <div className="absolute right-[7%] bottom-8 hidden h-44 w-44 rounded-full border border-nosh-gold/25 lg:block" />
        <div className="absolute left-[43%] top-24 hidden h-24 w-24 rounded-full border border-nosh-green/15 lg:block" />
      </div>

      <div className="relative z-10 mx-auto grid max-w-[1300px] items-center gap-10 px-5 pb-10 md:px-10 lg:grid-cols-[1.02fr_0.98fr] lg:gap-14 lg:pb-16">
        {/* Left */}
        <motion.div initial="hidden" animate="show">
          <motion.div variants={fade} custom={0} className="mb-2 flex items-center gap-2">
            <span className="font-script text-2xl text-nosh-gold">Authentic desi food</span>
            <Heart className="h-4 w-4 fill-nosh-gold text-nosh-gold" />
          </motion.div>

          <h1 className="font-display text-4xl font-bold leading-[1.03] tracking-tight text-nosh-ink sm:text-5xl lg:text-6xl">
            <motion.span variants={fade} custom={1} className="block">Made with</motion.span>
            <motion.span variants={fade} custom={2} className="block">
              <span className="font-script text-[1.15em] font-semibold text-nosh-gold">pyaar</span>
              <span className="text-nosh-green">.</span>
            </motion.span>
          </h1>

          <motion.p variants={fade} custom={3} className="mt-4 max-w-lg font-body text-base leading-relaxed text-nosh-ink/70">
            From the kitchen of <span className="font-semibold text-nosh-ink">Harris &amp; Kanwal</span> to
            your table — biryani, nihari, karahi and every recipe our elders taught us, catered across New
            Jersey &amp; New York.
          </motion.p>

          <motion.div variants={fade} custom={4} className="mt-6 flex flex-wrap items-center gap-3">
            <Link to="/menu" data-testid="hero-menu-cta" className="group flex items-center gap-2.5 rounded-full bg-nosh-green px-6 py-3.5 font-poster text-sm font-bold uppercase tracking-wide text-nosh-cream transition-transform duration-300 hover:-translate-y-1">
              Explore Menu
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link to="/menu#builder" data-testid="hero-build-cta" className="flex items-center gap-2.5 rounded-full border-2 border-nosh-ink/20 px-6 py-3.5 font-poster text-sm font-bold uppercase tracking-wide text-nosh-ink transition-colors duration-300 hover:border-nosh-green hover:text-nosh-green">
              <Utensils className="h-4 w-4" />
              Build Your Tray
            </Link>
          </motion.div>

          <motion.div variants={fade} custom={5} className="mt-8 grid max-w-lg grid-cols-2 gap-4 sm:grid-cols-4">
            {BADGES.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="flex items-center gap-2.5">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-nosh-border bg-white text-nosh-gold">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="leading-tight">
                    <div className="font-poster text-[11px] font-bold uppercase tracking-wide text-nosh-ink">{b.title}</div>
                    <div className="font-poster text-[10px] uppercase tracking-wide text-nosh-ink/50">{b.sub}</div>
                  </div>
                </div>
              );
            })}
          </motion.div>

          {/* Green trust card — fills space + adds brand green */}
          <motion.div variants={fade} custom={6} className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-2xl bg-nosh-green px-6 py-4 text-nosh-cream shadow-lg">
            <div className="flex items-center gap-2">
              <div className="flex text-nosh-gold">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-nosh-gold" />)}
              </div>
              <span className="font-display text-lg font-bold">5.0</span>
            </div>
            <div className="h-8 w-px bg-nosh-cream/20" />
            <div className="leading-tight">
              <div className="font-display text-lg font-bold text-nosh-gold">1000+ guests</div>
              <div className="font-poster text-[11px] uppercase tracking-wide text-nosh-cream/75">served across NJ &amp; NY</div>
            </div>
            <div className="hidden h-8 w-px bg-nosh-cream/20 sm:block" />
            <div className="hidden leading-tight sm:block">
              <div className="font-display text-lg font-bold text-nosh-gold">40+ dishes</div>
              <div className="font-poster text-[11px] uppercase tracking-wide text-nosh-cream/75">weddings &amp; events</div>
            </div>
          </motion.div>

          {/* Spice strip — fills space with real spice imagery */}
          <motion.div variants={fade} custom={7} className="mt-6 flex items-center gap-3">
            <div className="flex -space-x-3">
              {DECOR.map((s, i) => (
                <span key={i} className="h-11 w-11 overflow-hidden rounded-full border-2 border-nosh-cream shadow-sm ring-1 ring-nosh-border">
                  <img src={s} alt="" className="h-full w-full object-cover" />
                </span>
              ))}
            </div>
            <span className="font-body text-sm text-nosh-ink/60">
              Every tray made with <span className="font-semibold text-nosh-green">20+ hand-ground spices</span>
            </span>
          </motion.div>
        </motion.div>

        {/* Right — rotating gallery */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
          className="relative mx-auto w-full max-w-sm lg:max-w-none"
        >
          <motion.div style={{ y: imgY }} className="relative aspect-[4/5] overflow-hidden rounded-[1.75rem] border border-nosh-border shadow-[0_24px_50px_-22px_rgba(36,32,26,0.35)] lg:aspect-[5/5]">
            <AnimatePresence mode="popLayout">
              <motion.img
                key={active.src}
                src={active.src}
                alt={active.alt}
                data-testid="hero-gallery-image"
                initial={{ opacity: 0, scale: 1.08 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </AnimatePresence>

            {/* dot controls */}
            <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-2 rounded-full bg-nosh-ink/35 px-3 py-1.5 backdrop-blur">
              {HERO_IMAGES.map((im, i) => (
                <button
                  key={im.src}
                  onClick={() => setIdx(i)}
                  aria-label={`Show ${im.name}`}
                  data-testid={`hero-dot-${i}`}
                  className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-nosh-gold" : "w-2 bg-nosh-cream/70 hover:bg-nosh-cream"}`}
                />
              ))}
            </div>
          </motion.div>

          {/* Signature card synced to gallery */}
          <div className="absolute -bottom-5 left-3 flex min-w-[180px] items-center gap-3 rounded-2xl border border-nosh-border bg-white/95 px-4 py-3 shadow-xl backdrop-blur md:-left-5">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-nosh-green/10 font-display text-base font-bold text-nosh-green">★</div>
            <AnimatePresence mode="wait">
              <motion.div
                key={active.name}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35 }}
                className="leading-tight"
              >
                <div className="font-poster text-[10px] font-bold uppercase tracking-widest text-nosh-gold">Signature</div>
                <div className="font-display text-base font-bold text-nosh-ink">{active.name}</div>
                <div className="font-body text-xs text-nosh-ink/55">{active.price}</div>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
